import { useCallback } from 'react'
import { useAuth } from '../Context/AuthContext'
import { useCAWorkspace } from '../Context/CAWorkspaceContext'

/**
 * Centralised authenticated fetch hook.
 *
 * Contract:
 *   - Returns `null`     → session is dead; user has been logged out automatically.
 *   - Returns `Response` → for ALL other outcomes (2xx, 4xx, 5xx).
 *                          The caller checks `response.ok` and reads the body.
 *
 * Token refresh:
 *   - On a 401 the hook forces a real token refresh (bypasses the sessionStorage cache)
 *     then retries once.  If the retry still returns 401 the session is invalidated.
 *   - Uses the shared `refreshAccessToken(true)` so concurrent calls de-duplicate
 *     against the same in-flight refresh promise.
 */
export function useApiClient() {
  const { refreshAccessToken, logout } = useAuth()
  const workspace = useCAWorkspace()

  const authFetch = useCallback(async (url, options = {}) => {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
    const opts = {
      credentials: 'include',
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        // Inject workspace proxy header when operating inside a CA client workspace
        ...(workspace?.clientId ? { 'x-working-as-client-id': workspace.clientId } : {}),
        ...(options.headers || {}),
      },
    }

    let response = await fetch(url, opts)

    if (response.status === 401) {
      // Force a real network refresh — never use the sessionStorage cache here.
      const refreshed = await refreshAccessToken(true)
      if (!refreshed) {
        logout()
        return null
      }
      response = await fetch(url, opts)
      if (response.status === 401) {
        // Still 401 after a successful refresh — session is truly dead.
        logout()
        return null
      }
    }

    // Return the Response for ALL non-logout cases so callers can inspect status/body.
    return response
  }, [refreshAccessToken, logout, workspace?.clientId])

  return { authFetch }
}
